import * as React from 'react';

import { Ga4Events } from '@notino/web-tracking/dist/types/package/ga4/events';

import { useTrackingContext } from '@context/tracking/TrackingContext';

import {
  RICH_CONTENT_SECTION_PERCENTS,
  trackRichContentSectionView,
} from './tracking';

/** Time a section has to stay visible before we consider it "seen". */
const DWELL_MS = 2000;

/**
 * Tracks how far down the rich content the user gets. The rich content is
 * overlaid with three equally sized section markers (0-33%, 34-66%, 67-100%).
 * When a marker stays continuously visible for {@link DWELL_MS}, a single
 * `component_view` event is dispatched for that section.
 *
 * Tracking re-arms on `page_view` so it fires at most once per page view, even
 * during client-side navigation between products.
 */
export const useTrackRichContentSectionView = () => {
  const { getTimeFromInit } = useTrackingContext();
  const markerRefs = React.useRef<Array<HTMLElement | null>>(
    RICH_CONTENT_SECTION_PERCENTS.map(() => null)
  );

  React.useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const firedSections = new Set<number>();
    const dwellTimers: Array<ReturnType<typeof setTimeout> | undefined> = [];
    let hasScrolled = false;

    const markUserScrolled = () => {
      hasScrolled = true;
    };
    window.addEventListener('scroll', markUserScrolled, { passive: true });

    const fireSection = (index: number, target: Element) => {
      firedSections.add(index);
      trackRichContentSectionView({
        section: RICH_CONTENT_SECTION_PERCENTS[index],
        interaction: hasScrolled ? 'scroll' : 'automatic',
        timing: getTimeFromInit(),
      });
      observer.unobserve(target);
    };

    const handleEntry = (entry: IntersectionObserverEntry) => {
      const index = markerRefs.current.indexOf(entry.target as HTMLElement);
      if (index === -1 || firedSections.has(index)) {
        return;
      }

      if (entry.isIntersecting) {
        dwellTimers[index] = setTimeout(
          () => fireSection(index, entry.target),
          DWELL_MS
        );
      } else if (dwellTimers[index] !== undefined) {
        clearTimeout(dwellTimers[index]);
        dwellTimers[index] = undefined;
      }
    };

    const observer = new IntersectionObserver((entries) =>
      entries.forEach(handleEntry)
    );

    const observeSections = () => {
      markerRefs.current.forEach((element) => {
        if (element) {
          observer.observe(element);
        }
      });
    };
    observeSections();

    // A new page_view resets the session, so re-arm tracking for every section.
    const reArmOnPageView = ({ detail }: CustomEvent<Ga4Events>) => {
      if (detail?.event !== 'page_view') {
        return;
      }
      firedSections.clear();
      hasScrolled = false;
      dwellTimers.forEach(
        (timer) => timer !== undefined && clearTimeout(timer)
      );
      dwellTimers.length = 0;
      observeSections();
    };
    document.addEventListener(
      'ga4-tracking-event',
      reArmOnPageView as EventListener
    );

    return () => {
      observer.disconnect();
      dwellTimers.forEach(
        (timer) => timer !== undefined && clearTimeout(timer)
      );
      window.removeEventListener('scroll', markUserScrolled);
      document.removeEventListener(
        'ga4-tracking-event',
        reArmOnPageView as EventListener
      );
    };
  }, [getTimeFromInit]);

  const setMarkerRef = (index: number) => (element: HTMLElement | null) => {
    markerRefs.current[index] = element;
  };

  return { setMarkerRef };
};
